import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDownIcon, ChevronLeftIcon } from "@radix-ui/react-icons";
import * as React from "react";

import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn(className)} {...props} />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
    expandEventName?: string;
    isChevronLeft?: boolean;
    expandOnlyByChevron?: boolean;
    hideChevron?: boolean;
  }
>(
  (
    {
      className,
      children,
      expandEventName,
      isChevronLeft = false,
      expandOnlyByChevron = false,
      hideChevron = false,
      ...props
    },
    ref
  ) => {
    return (
      <AccordionPrimitive.Header className="flex w-full">
        {expandOnlyByChevron ? (
          <div
            className={cn(
              "flex flex-1 relative items-center justify-between text-sm text-left",
              className
            )}
          >
            <div className="flex-1 max-w-full">{children}</div>
            <AccordionPrimitive.Trigger
              ref={ref}
              className="h-4 w-4 shrink-0 absolute right-4 [&[data-state=open]>svg]:rotate-[-90deg]"
              {...props}
            >
              {!hideChevron && (
                <ChevronLeftIcon
                  className="transition-transform"
                  onClick={() => {
                    if (expandEventName) {
                      trackEvent({
                        eventName: expandEventName,
                      });
                    }
                  }}
                />
              )}
            </AccordionPrimitive.Trigger>
          </div>
        ) : (
          <AccordionPrimitive.Trigger
            ref={ref}
            className={cn(
              "flex flex-1 relative items-center justify-between text-sm [&[data-state=open]>svg]:rotate-180 text-left",
              className
            )}
            {...props}
          >
            {children}
            {!hideChevron && (
              <ChevronDownIcon
                className="h-4 w-4 shrink-0 absolute right-4 transition-transform"
                onClick={() => {
                  if (expandEventName) {
                    trackEvent({
                      eventName: expandEventName,
                    });
                  }
                }}
              />
            )}
          </AccordionPrimitive.Trigger>
        )}
      </AccordionPrimitive.Header>
    );
  }
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & {
    disableAnimation?: boolean;
  }
>(({ className, children, disableAnimation = false, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      "overflow-hidden text-sm",
      !disableAnimation &&
        "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    )}
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
